import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Spinner,
  Banner,
  DataTable,
  Badge,
  Box,
  Modal,
  FormLayout,
  TextField,
  Select,
  Pagination,
  EmptyState,
} from '@shopify/polaris';
import { DeleteIcon, EditIcon } from '@shopify/polaris-icons';

import { useAdminAuth } from '../../providers/AdminAuthProvider';

export default function AdminDiscounts() {
  const navigate = useNavigate();
  const { isAuthenticated, adminFetch } = useAdminAuth();
  const [discounts, setDiscounts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [editDiscountValue, setEditDiscountValue] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const loadDiscounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        per_page: 20,
      });

      const response = await adminFetch(`/api/admin/discounts?${params}`);
      if (response.ok) {
        const data = await response.json();
        setDiscounts(data.discounts);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      } else {
        setError('Failed to load discounts');
      }
    } catch (err) {
      setError('Failed to load discounts');
    } finally {
      setIsLoading(false);
    }
  }, [adminFetch, pagination.page]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    loadDiscounts();
  }, [isAuthenticated, navigate, loadDiscounts]);

  const openEditModal = (discount) => {
    setSelectedDiscount(discount);
    setEditDiscountValue(String(discount.discount_value));
    setEditDescription(discount.description || '');
    setEditIsActive(discount.is_active);
    setShowEditModal(true);
  };

  const handleUpdateDiscount = async () => {
    if (!selectedDiscount) return;
    setActionLoading(true);
    try {
      const response = await adminFetch(`/api/admin/discounts/${selectedDiscount.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount_value: parseFloat(editDiscountValue),
          description: editDescription,
          is_active: editIsActive,
        }),
      });
      if (response.ok) {
        setShowEditModal(false);
        setSuccess('Discount updated successfully');
        loadDiscounts();
      } else {
        setError('Failed to update discount');
      }
    } catch (err) {
      setError('Failed to update discount');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteDiscount = async (discount) => {
    if (!window.confirm(`Delete discount for ${discount.shop_domain}?`)) return;
    try {
      const response = await adminFetch(`/api/admin/discounts/${discount.id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setSuccess('Discount deleted');
        loadDiscounts();
      } else {
        setError('Failed to delete discount');
      }
    } catch (err) {
      setError('Failed to delete discount');
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const formatDiscountValue = (discount) => {
    if (discount.discount_type === 'percentage') return `${discount.discount_value}%`;
    if (discount.discount_type === 'free_trial') return `${discount.discount_value} days`;
    return `$${discount.discount_value}`;
  };

  const discountRows = discounts.map(discount => [
    discount.shop_domain || '-',
    <Badge tone={
      discount.discount_type === 'percentage' ? 'info' :
      discount.discount_type === 'fixed' ? 'success' : 'attention'
    }>
      {discount.discount_type}
    </Badge>,
    formatDiscountValue(discount),
    discount.description || '-',
    <Badge tone={discount.is_active && discount.is_valid ? 'success' : 'critical'}>
      {discount.is_active && discount.is_valid ? 'Active' : 'Inactive'}
    </Badge>,
    discount.times_used || 0,
    discount.expires_at ? new Date(discount.expires_at).toLocaleDateString() : 'Never',
    discount.created_at ? new Date(discount.created_at).toLocaleDateString() : '-',
    <InlineStack gap="200">
      <Button size="slim" icon={EditIcon} onClick={() => openEditModal(discount)}>
        Edit
      </Button>
      <Button size="slim" icon={DeleteIcon} tone="critical" onClick={() => handleDeleteDiscount(discount)}>
        Delete
      </Button>
    </InlineStack>,
  ]);

  return (
    <Page
      title="Discounts"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/admin/dashboard') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}
        
        {success && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccess(null)}>
              {success}
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            {isLoading ? (
              <Box padding="800">
                <InlineStack align="center">
                  <Spinner size="large" />
                </InlineStack>
              </Box>
            ) : discounts.length === 0 ? (
              <EmptyState
                heading="No discounts yet"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Create discounts from the Stores page by selecting a store and clicking "Discount".</p>
                <Button onClick={() => navigate('/admin/stores')}>
                  Go to Stores
                </Button>
              </EmptyState>
            ) : (
              <BlockStack gap="400">
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'text', 'text']}
                  headings={['Store', 'Type', 'Value', 'Description', 'Status', 'Used', 'Expires', 'Created', 'Actions']}
                  rows={discountRows}
                />
                {pagination.pages > 1 && (
                  <InlineStack align="center">
                    <Pagination
                      hasPrevious={pagination.page > 1}
                      hasNext={pagination.page < pagination.pages}
                      onPrevious={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      onNext={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    />
                  </InlineStack>
                )}
                <Text tone="subdued">
                  Showing {discounts.length} of {pagination.total} discounts
                </Text>
              </BlockStack>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Edit Modal */}
      <Modal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Discount"
        primaryAction={{
          content: 'Save Changes',
          onAction: handleUpdateDiscount,
          loading: actionLoading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowEditModal(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label={
                selectedDiscount?.discount_type === 'percentage'
                  ? 'Discount (%)'
                  : selectedDiscount?.discount_type === 'free_trial'
                    ? 'Extra Trial Days'
                    : 'Discount ($)'
              }
              type="number"
              value={editDiscountValue}
              onChange={setEditDiscountValue}
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={setEditDescription}
            />
            <Select
              label="Status"
              options={[
                { label: 'Active', value: 'true' },
                { label: 'Inactive', value: 'false' },
              ]}
              value={String(editIsActive)}
              onChange={(v) => setEditIsActive(v === 'true')}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
